import { Link } from 'react-router';
import type { ShareDraft } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { useStore } from '../state/store.tsx';
import { Icon } from './Icon.tsx';

const KNOWN: Record<string, string> = { claude: 'Claude', chatgpt: 'ChatGPT', openai: 'ChatGPT' };

/** Who prepared a draft: the connector's client name in `createdVia` (older drafts say `mcp:<client>`). */
export function preparedBy(createdVia: string | null | undefined): string {
  const client = (createdVia ?? '').replace(/^mcp:/, '').trim();
  if (!client) return t('drafts.assistant');
  return KNOWN[client.toLowerCase()] ?? client;
}

/** Shares an assistant prepared and is waiting for the person to confirm. Nothing shows when there are none. */
export function DraftBanner({ className = '' }: { className?: string }) {
  const { drafts } = useStore();
  if (drafts.length === 0) return null;
  const first = drafts[0]!;
  const names = [...new Set(drafts.map((d) => preparedBy(d.createdVia)))];
  const name = names.length === 1 ? names[0]! : t('drafts.assistant');
  return (
    <section className={`draft-banner ${className}`} aria-label={t('draft.eyebrow')}>
      <span className="draft-banner__icon" aria-hidden="true"><Icon name="sparkle" size={18} /></span>
      <div className="draft-banner__body">
        <p className="draft-banner__text">
          {drafts.length === 1 ? t('drafts.banner', { name }) : t('drafts.bannerMany', { name, n: drafts.length })}
        </p>
        {drafts.length > 1 && (
          <ul className="draft-banner__list">
            {drafts.map((d) => <DraftLink key={d.id} draft={d} />)}
          </ul>
        )}
      </div>
      {drafts.length === 1 && (
        <Link to={`/share/confirm/${encodeURIComponent(first.id)}`} className="button button--sm button--outline-mint draft-banner__cta" aria-label={t('drafts.reviewLabel', { title: first.title })}>
          {t('drafts.review')}<Icon name="arrowRight" size={14} />
        </Link>
      )}
    </section>
  );
}

function DraftLink({ draft }: { draft: ShareDraft }) {
  return (
    <li>
      <Link to={`/share/confirm/${encodeURIComponent(draft.id)}`} className="table-link" aria-label={t('drafts.reviewLabel', { title: draft.title })}>
        {draft.title}
      </Link>
      <span className="muted"> · {t(`shareNew.kind.${draft.kind}`)} · {draft.planName}</span>
    </li>
  );
}
